import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface Shop {
  shopId: string;
  shop: string;
  plan: string;
  subscription: string;
  hasApiKey: boolean;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  accessToken: string | null;
  shop: Shop | null;
}

const SESSION_KEY = 'accessToken';
const SHOP_KEY = 'shop';

function loadFromSession(): Partial<AuthState> {
  try {
    const accessToken = sessionStorage.getItem(SESSION_KEY);
    const shopData = sessionStorage.getItem(SHOP_KEY);
    if (accessToken && shopData) {
      const shop = JSON.parse(shopData) as Shop;
      return {
        isAuthenticated: true,
        isLoading: false,
        accessToken,
        shop,
      };
    }
  } catch {
    // Ignore errors
  }
  return {};
}

function saveToSession(accessToken: string, shop: Shop) {
  try {
    sessionStorage.setItem(SESSION_KEY, accessToken);
    sessionStorage.setItem(SHOP_KEY, JSON.stringify(shop));
  } catch {
    // Ignore errors
  }
}

function updateShopInSession(shop: Shop) {
  try {
    sessionStorage.setItem(SHOP_KEY, JSON.stringify(shop));
  } catch {
    // Ignore errors
  }
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SHOP_KEY);
  } catch {
    // Ignore errors
  }
}

const storedAuth = loadFromSession();

const initialState: AuthState = {
  isAuthenticated: storedAuth.isAuthenticated ?? false,
  isLoading: storedAuth.isAuthenticated ? false : true,
  error: null,
  accessToken: storedAuth.accessToken ?? null,
  shop: storedAuth.shop ?? null,
};

interface LoginPayload {
  shopId: string;
  idToken: string;
}

interface LoginResponse {
  accessToken: string;
  hasApiKey: boolean;
  shopId: string;
  shop: string;
  plan: string;
  subscription: string;
}

export const login = createAsyncThunk<LoginResponse, LoginPayload>(
  'auth/login',
  async ({ shopId, idToken }, { rejectWithValue }) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ shopId, idToken }),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || 'Login failed');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Network error'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.accessToken = null;
      state.shop = null;
      state.error = null;
      clearSession();
    },
    clearError: (state) => {
      state.error = null;
    },
    setHasApiKey: (state, action: PayloadAction<boolean>) => {
      if (state.shop) {
        state.shop.hasApiKey = action.payload;
        updateShopInSession(state.shop);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        const shop: Shop = {
          shopId: action.payload.shopId,
          shop: action.payload.shop,
          plan: action.payload.plan,
          subscription: action.payload.subscription,
          hasApiKey: action.payload.hasApiKey,
        };
        state.shop = shop;
        saveToSession(action.payload.accessToken, shop);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError, setHasApiKey } = authSlice.actions;
export default authSlice.reducer;
